import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Search,
  Users,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  Eye,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Heart,
  Building2,
  TrendingUp,
  MessageCircle,
  Send,
  Banknote,
  Sparkles,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (value) => {
  if (!value && value !== 0) return '$ 0';
  return `$ ${Math.round(value).toLocaleString('es-CO')}`;
};

const TIPOS_REFERIDO = {
  novio: { label: 'Novio', color: 'bg-[#D4AF37]', comision: '5%' },
  invitado: { label: 'Invitado', color: 'bg-blue-600', comision: '3%' },
  mascota: { label: 'Mascota', color: 'bg-pink-600', comision: '3%' },
  paje: { label: 'Paje', color: 'bg-purple-600', comision: '3%' },
  corte_completa: { label: 'Corte Completa', color: 'bg-emerald-600', comision: '5%' },
  otro: { label: 'Otro', color: 'bg-zinc-600', comision: '2%' }
};

const ESTADOS_REFERIDO = {
  registrado: { label: 'Registrado', color: 'bg-zinc-600', icon: Clock },
  pendiente_contacto: { label: 'Pendiente contacto', color: 'bg-amber-600', icon: Clock },
  contactado: { label: 'Contactado', color: 'bg-blue-600', icon: CheckCircle },
  cotizado: { label: 'Cotizado', color: 'bg-purple-600', icon: FileText },
  venta_realizada: { label: 'Venta realizada', color: 'bg-green-600', icon: CheckCircle },
  no_fue_venta: { label: 'No fue venta', color: 'bg-red-600', icon: XCircle },
  listo_cobro: { label: 'Listo para cobro', color: 'bg-[#D4AF37]', icon: DollarSign },
  pagado: { label: 'Pagado', color: 'bg-emerald-600', icon: CheckCircle }
};

const TIPOS_ALIADO = {
  wedding_planner: 'Wedding Planner',
  hotel: 'Hotel',
  hacienda: 'Hacienda',
  fotografo: 'Fotógrafo',
  organizador_eventos: 'Organizador',
  productora: 'Productora',
  proveedor: 'Proveedor',
  aliado_bodas: 'Aliado Bodas',
  otro: 'Otro'
};

// Componente de acción para aprobar/notificar comisiones
const ComisionActions = ({ referido, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handleAprobar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/aliados/comisiones/${referido.id}/aprobar`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Comisión aprobada');
      if (response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      }
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aprobar');
    } finally {
      setLoading(false);
      setShowMenu(false);
    }
  };

  const handleMarcarPagada = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/aliados/comisiones/${referido.id}/estado`,
        { estado_comision: 'pagada' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Comisión marcada como pagada');
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
      setShowMenu(false);
    }
  };

  const handleEnviarWhatsApp = async (tipo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/aliados/comisiones/${referido.id}/notificar-whatsapp?tipo_notificacion=${tipo}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.whatsapp_url) {
        window.open(response.data.whatsapp_url, '_blank');
      }
    } catch (error) {
      toast.error('Error al generar mensaje');
    }
    setShowMenu(false);
  };

  const estadoComision = referido.estado_comision || 'pendiente';
  const tieneComision = referido.valor_comision > 0;

  if (!tieneComision) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={loading}
        className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        ) : (
          <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
        )}
      </button>
      
      {showMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
          <div className="absolute right-0 top-8 z-50 w-52 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl py-1">
            {(estadoComision === 'pendiente' || estadoComision === 'calculada') && (
              <button
                onClick={handleAprobar}
                className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-zinc-800 text-sm"
              >
                <CheckCircle className="w-4 h-4 text-green-500" />
                Aprobar y Notificar
              </button>
            )}
            {estadoComision === 'aprobada' && (
              <>
                <button
                  onClick={handleMarcarPagada}
                  className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-zinc-800 text-sm"
                >
                  <Banknote className="w-4 h-4 text-green-500" />
                  Marcar Pagada
                </button>
                <button
                  onClick={() => handleEnviarWhatsApp('pago')}
                  className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-zinc-800 text-sm"
                >
                  <Send className="w-4 h-4 text-green-500" />
                  Notificar Pago
                </button>
              </>
            )}
            <div className="border-t border-zinc-800 my-1" />
            <button
              onClick={() => handleEnviarWhatsApp('recordatorio')}
              className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-zinc-800 text-sm"
            >
              <MessageCircle className="w-4 h-4 text-[#D4AF37]" />
              Enviar Recordatorio
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const ReferidosGlobal = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referidos, setReferidos] = useState([]);
  const [aliados, setAliados] = useState([]);
  const [search, setSearch] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterAliado, setFilterAliado] = useState('');
  const [filterEstadoComision, setFilterEstadoComision] = useState('');
  const [filterFueVenta, setFilterFueVenta] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Estado para eliminar referido
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [referidoAEliminar, setReferidoAEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  // Cargar lista de aliados para el filtro
  const fetchAliados = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/aliados`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAliados(response.data.aliados || []);
    } catch (error) {
      console.debug('Error al cargar aliados:', error.message);
    }
  }, []);

  useEffect(() => {
    fetchAliados();
  }, [fetchAliados]);

  const fetchReferidos = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterEstado) params.append('estado', filterEstado);
      if (filterTipo) params.append('tipo', filterTipo);
      if (filterAliado) params.append('aliado_id', filterAliado);
      if (filterEstadoComision) params.append('estado_comision', filterEstadoComision);
      if (filterFueVenta) params.append('fue_venta', filterFueVenta);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API_URL}/api/aliados/referidos/all?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferidos(response.data.referidos || []);
    } catch (error) {
      toast.error('Error al cargar los referidos');
    } finally {
      setLoading(false);
    }
  }, [filterEstado, filterTipo, filterAliado, filterEstadoComision, filterFueVenta, search]);

  useEffect(() => {
    fetchReferidos();
  }, [fetchReferidos]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReferidos();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, fetchReferidos]);

  // Limpiar todos los filtros
  const clearFilters = () => {
    setFilterEstado('');
    setFilterTipo('');
    setFilterAliado('');
    setFilterEstadoComision('');
    setFilterFueVenta('');
    setSearch('');
  };

  // Función para eliminar referido
  const eliminarReferido = async () => {
    if (!referidoAEliminar) return;
    
    setEliminando(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/aliados/referidos/${referidoAEliminar.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Referido "${referidoAEliminar.nombre}" eliminado correctamente`);
      setShowDeleteModal(false);
      setReferidoAEliminar(null);
      fetchReferidos();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al eliminar el referido';
      toast.error(errorMsg);
    } finally {
      setEliminando(false);
    }
  };

  // Abrir modal de confirmación para eliminar
  const confirmarEliminacionReferido = (e, referido) => {
    e.stopPropagation();
    setReferidoAEliminar(referido);
    setShowDeleteModal(true);
  };

  // Verificar si un referido puede ser eliminado
  const puedeEliminarReferido = (referido) => {
    // Solo Admin y Gerencia pueden eliminar
    if (!['admin', 'gerencia'].includes(user?.role)) return false;
    // No si tiene cuenta de cobro
    if (referido.id_cuenta_cobro) return false;
    return true;
  };

  const hasActiveFilters = filterEstado || filterTipo || filterAliado || filterEstadoComision || filterFueVenta || search;

  // Estadísticas
  const stats = {
    total: referidos.length,
    pendientes: referidos.filter(r => ['registrado', 'pendiente_contacto', 'contactado', 'cotizado'].includes(r.estado)).length,
    vendidos: referidos.filter(r => r.fue_venta === true || r.estado === 'venta_realizada').length,
    comisionTotal: referidos.reduce((sum, r) => sum + (r.valor_comision || 0), 0),
    comisionPendiente: referidos.filter(r => r.estado_comision !== 'pagada').reduce((sum, r) => sum + (r.valor_comision || 0), 0)
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{ backgroundImage: 'url(/images/novio-mascota.png)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/30 via-zinc-950/80 to-zinc-950" />
        
        <header className="relative bg-transparent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/aliados')}
                  className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="hidden sm:inline">Aliados</span>
                </button>
                <div className="h-6 w-px bg-zinc-700" />
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                  <h1 className="font-display text-xl">Gestión de Referidos</h1>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Stats */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="text-xs text-zinc-400">Total</p>
                  <p className="text-2xl font-display">{stats.total}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="text-xs text-zinc-400">Pendientes</p>
                  <p className="text-2xl font-display text-amber-400">{stats.pendientes}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <div>
                  <p className="text-xs text-zinc-400">Vendidos</p>
                  <p className="text-2xl font-display text-green-400">{stats.vendidos}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                <div>
                  <p className="text-xs text-zinc-400">Comisiones</p>
                  <p className="text-lg font-display text-[#D4AF37]">{formatPrice(stats.comisionTotal)}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-amber-400" />
                <div>
                  <p className="text-xs text-zinc-400">Por Pagar</p>
                  <p className="text-lg font-display text-amber-400">{formatPrice(stats.comisionPendiente)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filtros */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Buscar por nombre, teléfono o cédula..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/50 text-white placeholder-zinc-500"
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${
                showFilters || filterTipo || filterEstado
                  ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                  : 'border-zinc-700 hover:bg-zinc-800'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
            </button>
          </div>
          
          {showFilters && (
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-zinc-800">
              {/* Filtro por Aliado */}
              <select
                value={filterAliado}
                onChange={(e) => setFilterAliado(e.target.value)}
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white min-w-[180px]"
              >
                <option value="">Todos los aliados</option>
                {aliados.map((aliado) => (
                  <option key={aliado.id} value={aliado.id}>{aliado.nombre}</option>
                ))}
              </select>

              {/* Filtro por Tipo de Referido */}
              <select
                value={filterTipo}
                onChange={(e) => setFilterTipo(e.target.value)}
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value="">Todos los tipos</option>
                {Object.entries(TIPOS_REFERIDO).map(([key, value]) => (
                  <option key={key} value={key}>{value.label} ({value.comision})</option>
                ))}
              </select>
              
              {/* Filtro por Estado del Referido */}
              <select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value="">Todos los estados</option>
                {Object.entries(ESTADOS_REFERIDO).map(([key, value]) => (
                  <option key={key} value={key}>{value.label}</option>
                ))}
              </select>

              {/* Filtro por Estado de Comisión */}
              <select
                value={filterEstadoComision}
                onChange={(e) => setFilterEstadoComision(e.target.value)}
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value="">Todas las comisiones</option>
                <option value="pendiente">Pendiente</option>
                <option value="calculada">Calculada</option>
                <option value="aprobada">Aprobada</option>
                <option value="cuenta_cobro_generada">Cuenta de cobro</option>
                <option value="pagada">Pagada</option>
              </select>

              {/* Filtro por Venta */}
              <select
                value={filterFueVenta}
                onChange={(e) => setFilterFueVenta(e.target.value)}
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
              >
                <option value="">Todas las ventas</option>
                <option value="true">Fue venta</option>
                <option value="false">No fue venta</option>
                <option value="null">Pendiente</option>
              </select>
              
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 px-3 py-2 border border-red-500/30 rounded-lg hover:bg-red-500/10"
                >
                  <XCircle className="w-4 h-4" />
                  Limpiar filtros
                </button>
              )}
            </div>
          )}
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D4AF37]"></div>
          </div>
        ) : referidos.length === 0 ? (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-16 text-center">
            <Users className="w-16 h-16 mx-auto text-zinc-700 mb-4" />
            <h3 className="font-display text-xl mb-2">No hay referidos</h3>
            <p className="text-zinc-400">No se encontraron referidos con los filtros aplicados</p>
          </div>
        ) : (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-zinc-800/50">
                  <tr>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium text-sm">Referido</th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium text-sm">Aliado</th>
                    <th className="text-center py-3 px-4 text-zinc-400 font-medium text-sm">Tipo</th>
                    <th className="text-center py-3 px-4 text-zinc-400 font-medium text-sm">Estado</th>
                    <th className="text-right py-3 px-4 text-zinc-400 font-medium text-sm">Cotización</th>
                    <th className="text-right py-3 px-4 text-zinc-400 font-medium text-sm">Comisión</th>
                    <th className="text-center py-3 px-4 text-zinc-400 font-medium text-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {referidos.map((referido) => {
                    const tipoInfo = TIPOS_REFERIDO[referido.tipo_referido] || TIPOS_REFERIDO.otro;
                    const estadoInfo = ESTADOS_REFERIDO[referido.estado] || ESTADOS_REFERIDO.registrado;
                    const EstadoIcon = estadoInfo.icon;
                    
                    return (
                      <tr key={referido.id} className="hover:bg-zinc-800/30">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium">{referido.nombre}</p>
                            <div className="flex items-center gap-3 text-xs text-zinc-400">
                              {referido.telefono && (
                                <span className="flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {referido.telefono}
                                </span>
                              )}
                              {referido.fecha_evento && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {new Date(referido.fecha_evento).toLocaleDateString('es-CO')}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div 
                            className="cursor-pointer hover:text-[#D4AF37]"
                            onClick={() => navigate(`/aliados/${referido.id_aliado}`)}
                          >
                            <p className="font-medium text-sm">{referido.aliado_nombre || 'Sin aliado'}</p>
                            <p className="text-xs text-zinc-500">{TIPOS_ALIADO[referido.aliado_tipo] || referido.aliado_tipo}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs text-white ${tipoInfo.color}`}>
                            {tipoInfo.label}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-white ${estadoInfo.color}`}>
                            <EstadoIcon className="w-3 h-3" />
                            {estadoInfo.label}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          {referido.valor_cotizacion > 0 ? (
                            <div>
                              <p className="font-medium">{formatPrice(referido.valor_cotizacion)}</p>
                              <p className="text-xs text-zinc-500">
                                Base: {formatPrice(referido.valor_base_sin_iva || Math.round(referido.valor_cotizacion / 1.19))}
                              </p>
                            </div>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {referido.valor_comision > 0 ? (
                            <div>
                              <p className="text-[#D4AF37] font-medium">{formatPrice(referido.valor_comision)}</p>
                              <p className="text-xs text-zinc-500">{referido.porcentaje_comision}% s/base</p>
                              <span className={`text-xs ${
                                referido.estado_comision === 'pagada' ? 'text-green-400' :
                                referido.estado_comision === 'aprobada' ? 'text-[#D4AF37]' :
                                'text-zinc-500'
                              }`}>
                                {referido.estado_comision || 'pendiente'}
                              </span>
                            </div>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => navigate(`/aliados/${referido.id_aliado}/referidos/${referido.id}`)}
                              className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors"
                              title="Ver detalle"
                            >
                              <Eye className="w-4 h-4 text-zinc-400" />
                            </button>
                            <ComisionActions referido={referido} onUpdate={fetchReferidos} />
                            {/* Botón eliminar - Solo Admin/Gerencia */}
                            {puedeEliminarReferido(referido) && (
                              <button
                                onClick={(e) => confirmarEliminacionReferido(e, referido)}
                                className="p-1.5 hover:bg-red-500/20 rounded-lg transition-colors"
                                title="Eliminar referido"
                                data-testid={`eliminar-referido-${referido.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-red-400" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Modal de confirmación de eliminación de referido */}
      {showDeleteModal && referidoAEliminar && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="p-3 bg-red-500/20 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">Eliminar Referido</h3>
                  <p className="text-sm text-zinc-400">Esta acción no se puede deshacer</p>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-4 mb-4">
                <p className="text-white font-medium">{referidoAEliminar.nombre}</p>
                <p className="text-sm text-zinc-400">
                  {TIPOS_REFERIDO[referidoAEliminar.tipo_referido]?.label || referidoAEliminar.tipo_referido}
                  {referidoAEliminar.aliado_nombre && ` • ${referidoAEliminar.aliado_nombre}`}
                </p>
                {referidoAEliminar.id_cotizacion && (
                  <p className="text-xs text-amber-400 mt-2">
                    ⚠️ Este referido tiene una cotización vinculada que será desvinculada
                  </p>
                )}
                {referidoAEliminar.valor_comision > 0 && (
                  <p className="text-xs text-[#D4AF37] mt-1">
                    Comisión: {formatPrice(referidoAEliminar.valor_comision)}
                  </p>
                )}
              </div>
              
              <p className="text-sm text-zinc-400 mb-6">
                ¿Estás seguro de que deseas eliminar este referido? Los contadores del aliado y evento se actualizarán automáticamente.
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setReferidoAEliminar(null);
                  }}
                  className="flex-1 px-4 py-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors"
                  disabled={eliminando}
                >
                  Cancelar
                </button>
                <button
                  onClick={eliminarReferido}
                  disabled={eliminando}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-red-800 rounded-lg transition-colors"
                  data-testid="confirmar-eliminar-referido"
                >
                  {eliminando ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferidosGlobal;
